import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { exportToExcel, get, post } from '../../core/client';
import { ListProductsResponse, Product } from '../../models/query-member';
import { IGridPagedModel } from '../../models/shared/GridModels';
import WidgetTile from '../../components/widget-title';
import ExcelIcon from '../../components/excel-icon';
import Grid from '@mui/material/Grid';
import Input from '../../components/inputs/text-field-input';
import DatePicker from '../../components/inputs/date-picker';
import Select from '../../components/inputs/select';
import { ClearButton, CustomButton, SearchButton } from '../../components/buttons';
import DataTable from '../../components/data-table';
import { GridModel } from '../../core/models/GridModels';
import { DataTableCol } from '../../components/data-table/Models';

interface InputErrorModel {
    name: string;
    errorText: string;
}
interface SearchModel extends IGridPagedModel {
    no: string;
    avNo: string;
    productId: number | null;
    productName: string;
    beginDate: string;
    endDate: string;
}

interface TradeListItem {
    id: number;
    cancelDate: string;
    goldenCode: string;
    fullName: string;
    memberType: string;
    amount: string;
    discountAmount: string;
    avNo: string;
    tradeDate: string;
    tradeStatus: string;
    productName: string;
    relationType: string;
    invoiceFullName: string;
}

const initialGridState = {
    totalCount: 0,
    items: [],
};

const initialState: SearchModel = {
    no: '',
    avNo: '',
    productId: null,
    productName: '',
    beginDate: '',
    endDate: '',
    pageNumber: 0,
    pageSize: 5,
    order: false,
    orderBy: ''
};

const headCells: DataTableCol<TradeListItem>[] = [
    { id: 'goldenCode', label: 'Koç Ailem No', sortable: true },
    { id: 'fullName', label: 'Üye Adı SoyAdı', sortable: true },
    { id: 'memberType', label: 'Üye Tipi', sortable: true },
    { id: 'productName', label: 'Ürün/Hizmet', sortable: true },
    { id: 'amount', label: 'Net Ödenen', sortable: true },
    { id: 'discountAmount', label: 'İndirim Tutarı', sortable: true },
    { id: 'avNo', label: 'Alışveriş No', sortable: true },
    { id: 'tradeDate', label: 'Alışveriş Tarihi', sortable: true },
    { id: 'tradeStatus', label: 'Alışveriş Durumu', sortable: true },
    { id: 'cancelDate', label: 'İptal Tarihi', sortable: true },
    { id: 'relationType', label: 'Yakınlık Derecesi', sortable: true },
    { id: 'invoiceFullName', label: 'Fatura Ad Soyad', sortable: true },
];

const Search = () => {

    // states
    const [gridRequest, setGridRequest] = useState<SearchModel>(initialState);
    const [loading, setloading] = useState(false);
    const [inputErrors, setInputErrors] = useState<Array<InputErrorModel>>([]);
    const [excelLoading, setExcelLoading] = useState(false);
    const [gridData, setGridData] = useState<GridModel<TradeListItem>>(initialGridState);
    const [products, setProducts] = useState<Product[]>([]);

    //hooks
    useEffect(() => {
        queryProducts();
    }, []);

    //methods
    const loadGrid = async (request: SearchModel) => {

        if (!validate(request)) return;

        setloading(true);

        const req: SearchModel = {
            ...request,
            no: request.no ? request.no.toString() : '',
            avNo: request.avNo ? request.avNo.toString() : ''
        }
        const response = await post<any>('api/dealer/query-trades', req);
        setGridData(response);
        setloading(false);
    };

    const queryProducts = () => {
        get<ListProductsResponse>('api/portal/av-list-products', '').then((response: ListProductsResponse) => {
            setProducts(response.products);
        });
    };

    const handleProductChange = (e: any) => {
        const product = products.find(c => c.campaignProductId === e.target.value);
        if (product != null) {
            setGridRequest(prev => ({
                ...prev,
                productId: product.campaignProductId,
                productName: product.campaignProductName,
            }));
        }
    };

    const handleSorting = (event: any) => {
        const req = {
            ...gridRequest,
            orderBy: event.orderBy,
            order: event.order,
        }
        setGridRequest(req);
        loadGrid(req);
    };

    const handlePagination = (event: any) => {
        const req = {
            ...gridRequest,
            pageSize: event.pageSize,
            pageNumber: event.pageNumber,
        }
        setGridRequest(req);
        loadGrid(req);
    };

    const handleSearch = (request: SearchModel) => {
        const newReq: SearchModel = {
            ...request,
            pageNumber: 0
        };
        setGridRequest(newReq);
        loadGrid(newReq);
    }

    const handleChange = (e: any) => {
        setGridRequest(prevState => ({
            ...prevState,
            [e.target.name]: e.target.value,
        }));
    };

    const handleClear = () => {
        setGridRequest(initialState);
        setGridData(initialGridState);
        setInputErrors([]);
    }

    const validate = (model: SearchModel) => {

        setInputErrors([]);
        let isValid = true;

        if (model.beginDate && (model.endDate === null || model.endDate === '')) {
            const error: InputErrorModel = {
                name: 'endDate',
                errorText: 'Bu alan boş bırakılamaz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (model.endDate && (model.beginDate === null || model.beginDate === '')) {
            const error: InputErrorModel = {
                name: 'beginDate',
                errorText: 'Bu alan boş bırakılamaz'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (model.beginDate && model.endDate) {
            const beginDate = Date.parse(model.beginDate);
            const endDate = Date.parse(model.endDate);

            if (beginDate > endDate) {
                const error: InputErrorModel = {
                    name: 'beginDate',
                    errorText: 'Başlangıç tarihi bitiş tarihinden büyük olamaz'
                };
                setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
                isValid = false;
            }
        }
        if (model.no && model.no.toString().length !== 11) {
            const error: InputErrorModel = {
                name: 'no',
                errorText: '11 karakter uzunluğunda olmalıdır'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }
        if (model.avNo && model.avNo.length !== 20) {
            const error: InputErrorModel = {
                name: 'avNo',
                errorText: '20 karakter uzunluğunda olmalıdır'
            };
            setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
            isValid = false;
        }

        return isValid;
    }

    return (
        <div className="widget-area">
            <WidgetTile title="Satış Raporlama" showLoading={loading} />
            <div className="search">
                <Grid container spacing={2}>
                    <Grid item sm={12} lg={4}>
                        <Input
                            id="no"
                            name="no"
                            caption="TCKN/Koç Ailem No"
                            patternFormat={{
                                format: '###########'
                            }}
                            onChange={handleChange}
                            value={gridRequest.no}
                            errorText={inputErrors.find(x => x.name === 'no')?.errorText}
                        />
                    </Grid>
                    <Grid item sm={12} lg={4}>
                        <Input
                            id="avNo"
                            name="avNo"
                            caption="Alışveriş No"
                            patternFormat={{
                                format: '####################'
                            }}
                            onChange={handleChange}
                            value={gridRequest.avNo}
                            errorText={inputErrors.find(x => x.name === 'avNo')?.errorText}
                        />
                    </Grid>
                    <Grid item sm={12} lg={4}>
                        <Select<Product>
                            id="productId"
                            name="productId"
                            onChange={handleProductChange}
                            caption="Ürün/Hizmet"
                            className="densed-select"
                            options={{
                                data: products,
                                displayField: 'campaignProductName',
                                displayValue: 'campaignProductId',
                                selected: gridRequest.productId,
                            }} />
                    </Grid>
                    <Grid item sm={12} lg={4}>
                        <DatePicker
                            id="beginDate"
                            caption="Başlangıç Tarihi"
                            name="beginDate"
                            value={gridRequest.beginDate}
                            onChange={handleChange}
                            errorText={inputErrors.find(x => x.name === 'beginDate')?.errorText} />
                    </Grid>
                    <Grid item sm={12} lg={4}>
                        <DatePicker
                            id="endDate"
                            caption="Bitiş Tarihi"
                            name="endDate"
                            value={gridRequest.endDate}
                            onChange={handleChange}
                            errorText={inputErrors.find(x => x.name === 'endDate')?.errorText} />
                    </Grid>
                    <Grid item xs={12}>
                        <SearchButton
                            disabled={loading}
                            loading={loading}
                            className='mr-1'
                            onClick={() => handleSearch(gridRequest)}
                        />
                        <ClearButton
                            disabled={loading}
                            className="mr-1"
                            onClick={handleClear}
                        />
                        <CustomButton
                            text="İNDİR"
                            className='btn-secondary'
                            disabled={loading || excelLoading || gridData.items.length === 0}
                            loading={excelLoading}
                            onClick={async () => {
                                if (gridData.items.length > 0) {
                                    setExcelLoading(true);
                                    await exportToExcel('api/dealer/query-trades/export', gridRequest);
                                    setExcelLoading(false);
                                }
                            }}
                            icon={<ExcelIcon />}
                        />
                    </Grid>
                </Grid>
            </div>
            <div className="grid">
                <DataTable
                    data={gridData}
                    cells={headCells}
                    orderByOption={'id'}
                    orderOption={'desc'}
                    sortEvent={handleSorting}
                    paginationEvent={handlePagination}
                    pageNumber={gridRequest.pageNumber}
                />
            </div>
        </div>
    );
};

Search.propTypes = {
    change: PropTypes.bool,
};

export default Search;
